import React, { useEffect, useState } from 'react';
import { FaRoute, FaMapMarkerAlt, FaCalculator } from 'react-icons/fa';
import GoogleMapsService from '../services/googleMapsService';
import './DistanceCalculator.css';

const DistanceCalculator = ({ 
  pickupCoords, 
  dropoffCoords, 
  pickupLocation, 
  dropoffLocation, 
  onDistanceUpdate, 
  onCalculating 
}) => {
  const [distance, setDistance] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState(null);

  // Calculate distance using car routes only - prioritizes actual driving distances
  const calculateDistance = async (coords1, coords2) => {
    if (!coords1 || !coords2) return;

    try {
      setIsCalculating(true);
      onCalculating(true);
      setError(null);

      let distanceResult = null;

      // Try Google Maps Distance Matrix API first (optimized for car routes)
      if (GoogleMapsService.isConfigured()) {
        try {
          console.log('Using Google Maps Distance Matrix API for car route calculation');
          distanceResult = await GoogleMapsService.calculateDistance(coords1, coords2);
          console.log(`Car route distance: ${distanceResult.distance} km, Duration: ${distanceResult.duration}`);
          setDistance(distanceResult.distance);
          onDistanceUpdate(distanceResult.distance);
          return;
        } catch (googleError) {
          console.error('Google Maps car route API failed, trying fallback:', googleError);
        }
      }

      // Fallback to OpenRouteService driving-car API
      const drivingDistance = await getDrivingDistance(coords1, coords2);

      if (drivingDistance > 0) {
        console.log(`Using OpenRouteService driving-car API fallback: ${drivingDistance} km`);
        setDistance(drivingDistance);
        onDistanceUpdate(drivingDistance);
      } else {
        // Final fallback to Haversine formula with minimal multiplier
        console.log('Using Haversine formula with shortest route multiplier as final fallback');
        const straightDistance = calculateHaversineDistance(coords1, coords2);
        // Apply 1.1x multiplier for shortest driving distance
        const estimatedDrivingDistance = Math.round(straightDistance * 1.1 * 100) / 100;
        console.log(`Estimated shortest route distance: ${estimatedDrivingDistance} km (straight line: ${straightDistance} km)`);
        setDistance(estimatedDrivingDistance);
        onDistanceUpdate(estimatedDrivingDistance);
      }
    } catch (err) {
      console.error('Error calculating distance:', err);
      setError('Failed to calculate distance');
      setDistance(0);
      onDistanceUpdate(0);
    } finally {
      setIsCalculating(false);
      onCalculating(false);
    }
  };

  // Get driving distance using OpenRouteService API - optimized for car/taxi routes
  const getDrivingDistance = async (coords1, coords2) => {
    try {
      const apiKey = process.env.REACT_APP_OPENROUTE_API_KEY || '5b3ce3597851110001cf6248a8b8c8b8b8b8b8b8';

      console.log(`[OpenRouteService] Calculating car route from:`, coords1, 'to:', coords2);

      const response = await fetch(
        `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            coordinates: [
              [coords1.lng, coords1.lat],
              [coords2.lng, coords2.lat]
            ],
            units: 'km',
            geometry: true,
            instructions: false,
            preference: 'shortest', // Optimize for shortest distance
            options: {
              avoid_features: ['ferries'], // Avoid ferries for car routes
              vehicle_type: 'car'
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const distance = data.features[0].properties.summary.distance;
        const duration = data.features[0].properties.summary.duration;
        console.log(`[OpenRouteService] Car route calculated: ${distance} km, Duration: ${Math.round(duration/60)} minutes`);
        return Math.round(distance * 100) / 100; // Round to 2 decimal places
      }
      
      return 0;
    } catch (error) {
      console.error('OpenRouteService API error:', error);
      return 0;
    }
  };

  // Calculate straight line distance using Haversine formula
  const calculateHaversineDistance = (coords1, coords2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (coords2.lat - coords1.lat) * Math.PI / 180;
    const dLon = (coords2.lng - coords1.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(coords1.lat * Math.PI / 180) * Math.cos(coords2.lat * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return Math.round(distance * 100) / 100;
  };

  // Calculate distance when both coordinates are available
  useEffect(() => {
    if (pickupCoords && dropoffCoords) {
      calculateDistance(pickupCoords, dropoffCoords);
    } else {
      setDistance(0);
      onDistanceUpdate(0);
    }
  }, [pickupCoords, dropoffCoords]);

  if (error) {
    return (
      <div className="distance-calculator-error">
        <FaCalculator className="error-icon" />
        <p>{error}</p>
        <small>Using approximate distance calculation</small>
      </div>
    );
  }

  return (
    <div className="distance-calculator">
      <div className="calculator-header">
        <div className="header-left">
          <FaRoute className="calculator-icon" />
          <span>Distance Calculator</span>
        </div>
        {pickupLocation && dropoffLocation && (
          <div className="distance-status">
            {isCalculating ? (
              <div className="calculating">
                <div className="loading-spinner-small"></div>
                <span>Calculating...</span>
              </div>
            ) : (
              <span className="distance-value">{distance} km</span>
            )}
          </div>
        )}
      </div>
      
      <div className="location-display">
        {pickupLocation && (
          <div className="location-item pickup">
            <FaMapMarkerAlt className="location-icon pickup-icon" />
            <span className="location-text">{pickupLocation}</span>
          </div>
        )}
        
        {pickupLocation && dropoffLocation && (
          <div className="route-separator">
            <div className="route-line"></div>
            <div className="route-distance">
              {isCalculating ? (
                <div className="loading-spinner-small"></div>
              ) : (
                `${distance} km`
              )}
            </div>
            <div className="route-line"></div>
          </div>
        )}
        
        {dropoffLocation && (
          <div className="location-item dropoff">
            <FaMapMarkerAlt className="location-icon dropoff-icon" />
            <span className="location-text">{dropoffLocation}</span>
          </div>
        )}
      </div>
      
      {!pickupLocation || !dropoffLocation ? (
        <div className="calculator-instructions">
          <p>📍 Select pickup and drop-off locations above to calculate distance</p>
        </div>
      ) : null}
    </div>
  );
};

export default DistanceCalculator;